/**
 * RealUserInfoContext
 *
 * This module provides a React Context for managing and fetching real user information
 * from an external API. It integrates with Okta authentication to retrieve user data
 * based on authenticated user claims.
 *
 * Components:
 * - RealUserInfoProvider: Context provider component that fetches and manages user info
 * - useGetRealUserInfo: Custom hook to access and transform user info from the context
 *
 * Features:
 * - Fetches user information from the n8n webhook API through the proxy endpoint
 * - Integrates with Okta authentication to resolve the current user's identity
 * - Supports lock-aware fetching via the provided `lockAwareFetch` dependency
 * - Normalizes user lookups by stripping apostrophes from the value before request creation
 * - Retries the API call once using the authenticated user's email when the initial name-based lookup fails
 * - Prevents duplicate email retry requests with a module-level guard
 * - Provides console-based error logging and optional callback transformation for consumers
 */

import { createContext, useContext, useState, useEffect } from 'react'
import { useOktaAuth } from '@okta/okta-react'

const UserInfoContext = createContext(null)

// Prevents repeated email-based fallback requests when the initial name-based lookup fails.
let isAPITriggeredWithEmail = false

export default function RealUserInfoProvider({ children, lockAwareFetch }) {
  const [userInfo, setUserInfo] = useState({})

  const { authState } = useOktaAuth()

  useEffect(() => {
    const fetchUserInfo = async userVal => {
      const value = String(userVal ?? '').trim()

      if (!value) {
        return
      }

      // The webhook expects a sanitized identifier string without apostrophes.
      const safeVal = String(userVal).replace(/'/g, '')
      const searchParams = `?${new URLSearchParams({
        val: safeVal,
        override: false,
      }).toString()}`

      lockAwareFetch(`${window.env?.VITE_IAR_API}/proxy-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          url: `${window.env.n8nApiUrl}/webhook/public/isa/getUserInfo2${searchParams}`,
          method: 'GET',
          payload: {},
        }),
      })
        .then(response => {
          console.log('Fetched real user info response:', response)
          if (Array.isArray(response) && response[0]) {
            setUserInfo(response[0])
          } else if (
            Object.keys(response || {}).length &&
            response.message === 'Unable to process proxy API request'
          ) {
            // Retry once with the user's email if the initial name-based lookup is rejected.
            if (!isAPITriggeredWithEmail) {
              isAPITriggeredWithEmail = true
              fetchUserInfo(authState?.idToken?.claims?.email)
            }
          }
        })
        .catch(error => {
          console.error('Error fetching real user info:', error)
        })
    }

    if (authState?.isAuthenticated) fetchUserInfo(authState?.idToken?.claims?.name)
  }, [authState?.isAuthenticated])

  return <UserInfoContext.Provider value={userInfo}>{children}</UserInfoContext.Provider>
}

export function useGetRealUserInfo(callback = obj => obj) {
  const context = useContext(UserInfoContext)

  if (!context) {
    throw new Error('useGetRealUserInfo must be used within a RealUserInfoProvider')
  }

  return callback(context)
}
